import React, { ReactElement } from 'react';
import { Label } from '@ui-library/typography';
import { Flex } from '@ui-library/misc';
import theme from '@css/theme';
import classes from './onboarding.module.scss';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const SampleSourceTarget = 'sample-source-target';
export const LiveEventsTarget = 'live-events-target';
export const SendEventTarget = 'send-event-target';

const getContent = (title: string, description: string | ReactElement) => (
  <Flex flexDirection="column" alignItems="flex-start">
    <Label fontSize="18px" color={theme.color.rsPrimary} className={classes.title}>
      {title}
    </Label>
    <Label
      fontWeight={400}
      fontSize="13px"
      secondary
      className={classes.description}
      lineHeight="18px"
    >
      {description}
    </Label>
  </Flex>
);

const styles = {
  tooltip: {
    borderRadius: 12,
    width: '500px',
  },
  buttonClose: {
    top: '10px',
    right: '10px',
    padding: '8px',
    color: 'black',
  },
  tooltipContent: {
    padding: 0,
  },
};

const commonProps = {
  disableScrolling: true,
  disableOverlayClose: true,
  spotlightPadding: 0,
};

export const sampleSourceProps = {
  steps: [
    {
      target: `.${SampleSourceTarget}`,
      content: getContent(
        'Let’s test some events!',
        'Welcome To RudderStack! To help you get started we created a sample HTTP source for you to easily test some events. Click on this sample source to get started.',
      ),
      disableBeacon: true,
      hideFooter: true,
      spotlightClicks: true,
    },
  ],
  ...commonProps,
  styles: {
    ...styles,
    tooltip: {
      ...styles.tooltip,
      padding: '24px 32px',
    },
    spotlight: {
      borderRadius: 20, // Same as card's
    },
  },
};

export const getSendEventProps = (onCopy: () => void) => ({
  steps: [
    {
      target: `.${SendEventTarget}`,
      content: getContent(
        'Copy this snippet to send an event',
        <>
          Paste this curl command in your terminal to send an event to RudderStack.{' '}
          <span className={classes.copy} onClick={onCopy}>
            Copy to clipboard
          </span>{' '}
          <FontAwesomeIcon icon={regular('copy')} className={classes.copy} onClick={onCopy} />
        </>,
      ),
      disableBeacon: true,
      hideFooter: true,
      spotlightClicks: true,
    },
  ],
  ...commonProps,
  spotlightPadding: 16,
  styles: {
    ...styles,
    tooltip: {
      ...styles.tooltip,
      padding: '24px 36px',
    },
  },
  callback: (data: { action: string; lifecycle: string }) => {
    if (data.action === 'close' && data.lifecycle === 'complete') {
      onCopy();
    }
  },
});

export const liveEventsProps = {
  steps: [
    {
      target: `.${LiveEventsTarget}`,
      content: getContent(
        'Start the test in our Live Events viewer',
        'Click the Live Events button above to learn how to send an event and see it immediately inside the Live Events viewer',
      ),
      disableBeacon: true,
      hideFooter: true,
      spotlightClicks: true,
    },
  ],
  ...commonProps,
  styles: {
    ...styles,
    tooltip: {
      ...styles.tooltip,
      padding: 24,
    },
  },
};

export const getLiveEventTabProps = (enable: boolean) => ({
  steps: [
    {
      target: enable ? '.ant-tabs-tab-active' : 'random-target-not-in-use',
      content: getContent(
        'Watch the live events come in',
        'Here you can see all your events as they flow through into RudderStack',
      ),
      disableBeacon: true,
      hideFooter: true,
    },
  ],
  ...commonProps,
  styles: {
    ...styles,
    tooltip: {
      ...styles.tooltip,
      padding: '24px 32px',
    },
  },
});
